/**
 * App shell: auth gate, six tabs, the add-food sheet, sync lifecycle.
 * Same shape as Lift's — no router, one ErrorBoundary per tab so a crash on one
 * screen never takes the tab bar (and the way to Settings/update) with it.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { ApiError, fetchMe, getToken, login, logout, type User } from './api/client'
import { OWNER_KEY, getMeta, setMeta, wipeLocal } from './db'
import { hydrateToken } from './native/storage'
import { startSyncLoop, subscribe, sync, type SyncState } from './sync/engine'
import { AppContext, type AddFoodRequest, type AppCtx } from './context'
import { AddFood } from './components/AddFood'
import { ErrorBoundary } from './components/ErrorBoundary'
import { UpdateBanner } from './components/UpdateBanner'
import { AndroidBanner } from './components/GetAndroid'
import { todayIso } from './lib/day'
import { DashboardScreen } from './screens/Dashboard'
import { LogScreen } from './screens/Log'
import { MoreScreen } from './screens/More'
import { PlanScreen } from './screens/Plan'
import { StatsScreen } from './screens/Stats'
import { TrendsScreen } from './screens/Trends'

type Tab = 'dashboard' | 'log' | 'plan' | 'trends' | 'stats' | 'more'

export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [booting, setBooting] = useState(true)
  const [tab, setTab] = useState<Tab>('dashboard')
  const [logDay, setLogDay] = useState(todayIso())
  const [adding, setAdding] = useState<AddFoodRequest | null>(null)

  useEffect(() => {
    let cancelled = false
    const finish = () => { if (!cancelled) setBooting(false) }
    void (async () => {
      try {
        await Promise.race([hydrateToken(), new Promise((r) => setTimeout(r, 2000))])
      } catch { /* no native storage */ }
      if (cancelled) return
      if (!getToken()) { finish(); return }
      try {
        const me = await fetchMe()
        if (!cancelled) setUser(me)
      } catch (e) {
        // Offline with a stored token: let them in on the cached identity, so
        // logging works in a kitchen with no signal.
        const cached = await getMeta<User | null>('auth.user', null)
        if (!cancelled && !(e instanceof ApiError) && cached) setUser(cached)
      } finally { finish() }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!user) return
    void setMeta('auth.user', user)
    return startSyncLoop()
  }, [user?.id])

  const handleSignedIn = useCallback(async (u: User) => {
    const previous = await getMeta<number | null>(OWNER_KEY, null)
    if (previous !== null && previous !== u.id) await wipeLocal()
    await setMeta(OWNER_KEY, u.id)
    setUser(u)
  }, [])

  const ctx: AppCtx | null = useMemo(() => user && {
    user, unit: user.unit_pref,
    openAdd: (req: AddFoodRequest) => setAdding(req),
    goTo: (t: Tab) => setTab(t),
  }, [user])

  if (booting) return <div className="wrap"><p className="muted">Loading…</p></div>
  if (!user || !ctx) return <SignIn onSignedIn={handleSignedIn} />

  return (
    <AppContext.Provider value={ctx}>
      <UpdateBanner />
      <AndroidBanner hasApp={!!user.has_android_app} />
      <SyncBanner />
      <ErrorBoundary key={tab}>
        <div className="wrap" style={{ paddingBottom: '6.5rem' }}>
          {tab === 'dashboard' && <DashboardScreen />}
          {tab === 'log' && <LogScreen day={logDay} setDay={setLogDay} />}
          {tab === 'plan' && <PlanScreen />}
          {tab === 'trends' && <TrendsScreen />}
          {tab === 'stats' && <StatsScreen />}
          {tab === 'more' && <MoreScreen onSignOut={async () => { await logout(); setUser(null) }} />}
        </div>
      </ErrorBoundary>
      {(tab === 'dashboard' || tab === 'log') && (
        <button className="fab" aria-label="Add food"
          onClick={() => setAdding({ day: tab === 'log' ? logDay : todayIso() })}>+</button>
      )}
      {adding && (
        <ErrorBoundary>
          <AddFood day={adding.day} meal={adding.meal} initial={adding.mode} onClose={() => setAdding(null)} />
        </ErrorBoundary>
      )}
      <TabBar tab={tab} onChange={(t) => { setTab(t); if (t === 'log') setLogDay((d) => d) }} />
    </AppContext.Provider>
  )
}

function SyncBanner() {
  const [state, setState] = useState<SyncState | null>(null)
  useEffect(() => subscribe(setState), [])
  if (!state || state.online) return null
  return (
    <div style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', padding: '.5rem 1rem',
      fontSize: '.85rem', textAlign: 'center' }}>
      Offline — {state.pending > 0 ? `${state.pending} change${state.pending === 1 ? '' : 's'} saved on this device` : 'your log is saved on this device'}
    </div>
  )
}

function TabBar({ tab, onChange }: { tab: Tab; onChange: (t: Tab) => void }) {
  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Today' },
    { id: 'log', label: 'Log' },
    { id: 'plan', label: 'Plan' },
    { id: 'trends', label: 'Trends' },
    { id: 'stats', label: 'Stats' },
    { id: 'more', label: 'More' },
  ]
  return (
    <nav style={{ position: 'fixed', left: 0, right: 0, bottom: 0, display: 'flex', background: 'var(--surface)',
      borderTop: '1px solid var(--border)', paddingBottom: 'env(safe-area-inset-bottom)', zIndex: 20 }}>
      {tabs.map((t) => (
        <button key={t.id} onClick={() => onChange(t.id)} aria-current={tab === t.id ? 'page' : undefined}
          style={{ flex: 1, margin: 0, borderRadius: 0, border: 'none', background: 'transparent',
            color: tab === t.id ? 'var(--accent)' : 'var(--muted)', fontWeight: tab === t.id ? 600 : 400,
            minHeight: 52, fontSize: '.76rem', padding: 0, minWidth: 0, whiteSpace: 'nowrap' }}>
          {t.label}
        </button>
      ))}
    </nav>
  )
}

function SignIn({ onSignedIn }: { onSignedIn: (u: User) => void }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      const u = await login(username, password, describeDevice())
      await onSignedIn(u)
      void sync()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't reach the server. Check your connection.")
    } finally { setBusy(false) }
  }

  return (
    <div className="wrap" style={{ maxWidth: '24rem' }}>
      <h1>Macro</h1>
      <form className="card" onSubmit={submit}>
        {error && <div className="error">{error}</div>}
        <label htmlFor="username">Username</label>
        <input id="username" autoComplete="username" autoCapitalize="none" autoCorrect="off"
          value={username} onChange={(e) => setUsername(e.target.value)} required />
        <label htmlFor="password">Password</label>
        <input id="password" type="password" autoComplete="current-password"
          value={password} onChange={(e) => setPassword(e.target.value)} required />
        <button type="submit" style={{ width: '100%' }} disabled={busy || !username || !password}>
          {busy ? 'Signing in…' : 'Sign in'}</button>
      </form>
      <p className="muted" style={{ marginTop: '1.5rem' }}>Sign in with your Lift account. Your weigh-ins are shared between the two apps.</p>
    </div>
  )
}

function describeDevice(): string {
  const ua = navigator.userAgent
  if (/Android/i.test(ua)) return 'Android'
  if (/iPhone|iPad/i.test(ua)) return 'iOS'
  if (/Macintosh/i.test(ua)) return 'Mac browser'
  if (/Windows/i.test(ua)) return 'Windows browser'
  return 'Browser'
}
